import React, { useState } from "react";
import Icon from "components/AppIcon";

const StandardProcessResults = ({ data, formatCurrency, formatNumber }) => {
  const [expandedSections, setExpandedSections] = useState({
    inputs: true,
    calculations: true,
    results: true,
  });

  if (!data) return null;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Helper function to format costs as positive values since they represent expenses
  const formatCost = (value) => {
    return formatCurrency(Math.abs(value));
  };

  const inputValues = [
    {
      label: "Material",
      value: data.standardProcess.material,
      icon: "Package",
    },
    {
      label: "Units",
      value: formatNumber(data.standardProcess.units),
      icon: "Hash",
    },
    {
      label: "Material Weight",
      value: `${formatNumber(data.standardProcess.materialWeight)} LBS`,
      icon: "Weight",
    },
  ];

  const hardcodedValues = [
    {
      label: "Fee Per Unit",
      value: formatCurrency(data.standardProcess.feePerUnit),
      icon: "DollarSign",
      bgColor: "bg-primary-50",
      borderColor: "border-primary-200",
      iconColor: "text-primary",
      labelColor: "text-text-primary",
      valueColor: "text-text-primary",
    },
    {
      label: "Transportation Estimate",
      value: `-${formatCurrency(
        Math.abs(data.standardProcess.transportationEstimate)
      )}`,
      icon: "Truck",
      bgColor: "bg-error-50",
      borderColor: "border-error-200",
      iconColor: "text-error",
      labelColor: "text-text-primary",
      valueColor: "text-error",
    },
  ];

  const calculationSteps = [
    {
      label: "Processing Fee",
      formula: "Material Weight × Fee Per Unit",
      calculation: `${formatNumber(
        data.standardProcess.materialWeight
      )} LBS × ${formatCurrency(Math.abs(data.standardProcess.feePerUnit))}`,
      result: Math.abs(data.standardProcess.processingFee),
      type: "currency",
      isNegative: true,
      valueColor: "text-error",
    },
    {
      label: "Transportation Estimate",
      formula: "Fixed Transportation Cost",
      calculation: "Hardcoded value",
      result: Math.abs(data.standardProcess.transportationEstimate),
      type: "currency",
      isNegative: true,
      valueColor: "text-error",
    },
    {
      label: "Project Total",
      formula: "Processing Fee + Transportation Estimate",
      calculation: `${formatCost(
        data.standardProcess.processingFee
      )} + ${formatCost(data.standardProcess.transportationEstimate)}`,
      result: Math.abs(data.standardProcess.projectTotal),
      type: "currency",
      highlight: true,
      isNegative: true,
      valueColor: "text-error",
    },
  ];

  const finalResults = [
    {
      label: "Project Total",
      value: Math.abs(data.standardProcess.projectTotal),
      type: "currency",
      icon: "Calculator",
      description: "Total cost including processing and transportation",
      isNegative: true,
      color: "text-error",
      bgColor: "bg-error-50",
      borderColor: "border-error-200",
      iconColor: "text-error",
    },
    {
      label: "Cost per Unit",
      value:
        Math.abs(data.standardProcess.projectTotal) /
        data.standardProcess.units,
      type: "currency",
      icon: "Package",
      description: "Project cost per individual unit",
    },
    {
      label: "Cost per Pound",
      value:
        Math.abs(data.standardProcess.projectTotal) /
        data.standardProcess.materialWeight,
      type: "currency",
      icon: "Weight",
      description: "Project cost per pound of material",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
          <Icon name="Settings" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-xl font-heading-semibold text-text-primary">
            Standard Process Results
          </h3>
          <p className="text-sm text-text-secondary">
            Textile processing cost analysis
          </p>
        </div>
      </div>

      {/* Input Values Section */}
      <div className="bg-surface-50 rounded-lg border border-border-light">
        <button
          onClick={() => toggleSection("inputs")}
          className="w-full px-4 py-3 flex items-center justify-between text-left focus-ring"
        >
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={16} className="text-text-secondary" />
            <span className="font-body-medium text-text-primary">
              Input Values
            </span>
          </div>
          <Icon
            name="ChevronDown"
            size={16}
            className={`text-text-tertiary transition-transform duration-300 ${
              expandedSections.inputs ? "rotate-180" : ""
            }`}
          />
        </button>

        {expandedSections.inputs && (
          <div className="px-4 pb-4 border-t border-border-light">
            <div className="space-y-4 mt-3">
              {/* User Inputs */}
              <div>
                <h4 className="text-sm font-body-medium text-text-primary mb-2">
                  User Inputs
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {inputValues.map((input, index) => (
                    <div
                      key={index}
                      className="flex items-center space-x-3 p-3 bg-surface rounded-lg border border-border"
                    >
                      <Icon
                        name={input.icon}
                        size={16}
                        className="text-text-secondary"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-body-medium text-text-primary">
                          {input.label}
                        </div>
                        <div className="text-sm text-text-secondary">
                          {input.value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Hardcoded Values */}
              <div>
                <h4 className="text-sm font-body-medium text-text-primary mb-2">
                  System Values
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {hardcodedValues.map((value, index) => (
                    <div
                      key={index}
                      className={`flex items-center space-x-3 p-3 rounded-lg border ${
                        value.bgColor && value.borderColor
                          ? `${value.bgColor} ${value.borderColor}`
                          : "bg-primary-50 border-primary-200"
                      }`}
                    >
                      <Icon
                        name={value.icon}
                        size={16}
                        className={value.iconColor || "text-primary"}
                      />
                      <div className="flex-1">
                        <div
                          className={`text-sm font-body-medium ${
                            value.labelColor || "text-primary"
                          }`}
                        >
                          {value.label}
                        </div>
                        <div
                          className={`text-sm ${
                            value.valueColor || "text-primary-600"
                          }`}
                        >
                          {value.value}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Calculation Steps Section */}
      <div className="bg-surface-50 rounded-lg border border-border-light">
        <button
          onClick={() => toggleSection("calculations")}
          className="w-full px-4 py-3 flex items-center justify-between text-left focus-ring"
        >
          <div className="flex items-center space-x-3">
            <Icon name="Calculator" size={16} className="text-text-secondary" />
            <span className="font-body-medium text-text-primary">
              Calculation Steps
            </span>
          </div>
          <Icon
            name="ChevronDown"
            size={16}
            className={`text-text-tertiary transition-transform duration-300 ${
              expandedSections.calculations ? "rotate-180" : ""
            }`}
          />
        </button>

        {expandedSections.calculations && (
          <div className="px-4 pb-4 border-t border-border-light">
            <div className="space-y-3 mt-3">
              {calculationSteps.map((step, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-lg border ${
                    step.highlight
                      ? "bg-accent-50 border-accent-200"
                      : "bg-surface border-border"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div
                        className={`font-body-medium ${
                          step.color ||
                          (step.highlight ? "text-accent" : "text-text-primary")
                        }`}
                      >
                        {step.label}
                      </div>
                      <div className="text-sm text-text-secondary mt-1">
                        {step.formula}
                      </div>
                      <div className="text-xs text-text-tertiary mt-1 font-data">
                        {step.calculation}
                      </div>
                    </div>
                    <div
                      className={`text-right ${
                        step.valueColor ||
                        step.color ||
                        (step.highlight ? "text-accent" : "text-text-primary")
                      }`}
                    >
                      <div
                        className={`font-data ${
                          step.highlight ? "text-lg" : "text-base"
                        }`}
                      >
                        {step.type === "currency"
                          ? step.isNegative
                            ? `-${formatCurrency(step.result)}`
                            : formatCurrency(step.result)
                          : formatNumber(step.result)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Final Results Section */}
      <div className="bg-surface-50 rounded-lg border border-border-light">
        <button
          onClick={() => toggleSection("results")}
          className="w-full px-4 py-3 flex items-center justify-between text-left focus-ring"
        >
          <div className="flex items-center space-x-3">
            <Icon name="BarChart3" size={16} className="text-text-secondary" />
            <span className="font-body-medium text-text-primary">
              Final Results
            </span>
          </div>
          <Icon
            name="ChevronDown"
            size={16}
            className={`text-text-tertiary transition-transform duration-300 ${
              expandedSections.results ? "rotate-180" : ""
            }`}
          />
        </button>

        {expandedSections.results && (
          <div className="px-4 pb-4 border-t border-border-light">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              {finalResults.map((result, index) => (
                <div
                  key={index}
                  className={`rounded-lg border p-4 ${
                    result.bgColor && result.borderColor
                      ? `${result.bgColor} ${result.borderColor}`
                      : "bg-surface border-border"
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        result.iconColor === "text-error"
                          ? "bg-error-100"
                          : "bg-primary-100"
                      }`}
                    >
                      <Icon
                        name={result.icon}
                        size={16}
                        className={result.iconColor || "text-primary"}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-body-medium text-text-primary">
                        {result.label}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`text-2xl font-data mb-1 ${
                      result.color || "text-primary"
                    }`}
                  >
                    {result.type === "currency"
                      ? result.isNegative
                        ? `-${formatCurrency(result.value)}`
                        : formatCurrency(result.value)
                      : `${formatNumber(result.value)}%`}
                  </div>
                  <div className="text-xs text-text-tertiary">
                    {result.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StandardProcessResults;
